import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  ThemeProvider,
  createTheme,
  TableSortLabel,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { pageColors } from '../pages/HomePage';
import apiFetch from '../api/apiFetch';

export default function InvoicesPage({ connected, setConnected }) {
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('documentNumber');

  useEffect(() => {
    console.log('InvoicesPage - connected on render:', connected);
  }, [connected]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const comparator = (a, b) => {
    let valA = a[orderBy];
    let valB = b[orderBy];
    if (typeof valA === 'string') {
      valA = valA.toLowerCase();
      valB = valB.toLowerCase();
    }
    if (valA < valB) return order === 'asc' ? -1 : 1;
    if (valA > valB) return order === 'asc' ? 1 : -1;
    return 0;
  };

  const sortedInvoices = [...invoices].sort(comparator);

  const handleSearch = async () => {
    console.log('InvoicesPage - connected before check:', connected);
    if (!connected) {
      alert('Not connected to Fortnox. Please connect first.');
      setConnected(false);
      return;
    }
    if (!startDate || !endDate) {
      alert('Please pick both start and end dates.');
      return;
    }

    if (dayjs(startDate).isAfter(dayjs(endDate))) {
      alert('Start date should be earlier than end date.');
      return;
    }

    const s = startDate.format('YYYY-MM-DD');
    const e = endDate.format('YYYY-MM-DD');
    try {
      const response = await apiFetch(`/api/invoices?startDate=${s}&endDate=${e}`);
      if (!response.ok) {
        const errorText = await response.text();
        let errMsg;
        if (response.status === 401) {
          errMsg = 'Lost connection to Fortnox: Unauthorized. Please reconnect.';
          setConnected(false);
        } else if (response.status === 429) {
          errMsg = "Fortnox cannot handle requests at the moment. Please wait.";
          setConnected(true);
        } else {
          errMsg = `Failed to fetch invoices: ${errorText || response.status}. Please check your connection and try again.`;
          setConnected(false);
        }
        alert(errMsg);
        return;
      }
      const data = await response.json();
      setInvoices(data);
      setConnected(true);
    } catch (err) {
      alert(err.message);
      setConnected(false);
    }
  };

  const invoicesTheme = createTheme({
    palette: {
      primary: {
        main: pageColors.invoices,
      },
    },
  });

  return (
    <ThemeProvider theme={invoicesTheme}>
      <Box
        sx={{
          backgroundColor: `${pageColors.invoices}15`,
          minHeight: '100vh',
          pt: { xs: 3, md: 4 },
          pb: { xs: 6, md: 8 },
          px: { xs: 1.5, sm: 3 },
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              sx={{
                color: pageColors.invoices,
                fontWeight: 'bold',
                mb: 3,
              }}
            >
              Invoices
            </Typography>

            <Paper
              elevation={3}
              sx={{
                p: 3,
                mb: 3,
                borderRadius: 2,
              }}
            >
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <DesktopDatePicker
                  label="Start Date"
                  inputFormat="YYYY-MM-DD"
                  value={startDate}
                  onChange={(newValue) => setStartDate(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" size="small" />
                  )}
                />
                <DesktopDatePicker
                  label="End Date"
                  inputFormat="YYYY-MM-DD"
                  value={endDate}
                  onChange={(newValue) => setEndDate(newValue)}
                  renderInput={(params) => (
                    <TextField {...params} variant="outlined" size="small" />
                  )}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  sx={{ alignSelf: 'center' }}
                >
                  Search
                </Button>
              </Box>
            </Paper>

            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                maxHeight: '70vh',
                overflowY: 'auto',
                mb: 4
              }}
            >
              <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }} aria-label="sticky invoices table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '20%' }}>
                      <TableSortLabel
                        active={orderBy === 'documentNumber'}
                        direction={orderBy === 'documentNumber' ? order : 'asc'}
                        onClick={() => handleRequestSort('documentNumber')}
                      >
                        <strong>Invoice #</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: '40%' }}>
                      <TableSortLabel
                        active={orderBy === 'customerName'}
                        direction={orderBy === 'customerName' ? order : 'asc'}
                        onClick={() => handleRequestSort('customerName')}
                      >
                        <strong>Customer</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: '20%' }}>
                      <TableSortLabel
                        active={orderBy === 'invoiceDate'}
                        direction={orderBy === 'invoiceDate' ? order : 'asc'}
                        onClick={() => handleRequestSort('invoiceDate')}
                      >
                        <strong>Invoice Date</strong>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ width: '20%' }}>
                      <TableSortLabel
                        active={orderBy === 'total'}
                        direction={orderBy === 'total' ? order : 'asc'}
                        onClick={() => handleRequestSort('total')}
                      >
                        <strong>Total</strong>
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedInvoices.length > 0 ? (
                    sortedInvoices.map((inv) => (
                      <TableRow key={inv.id || inv.documentNumber}>
                        <TableCell sx={{ width: '20%' }}>{inv.documentNumber}</TableCell>
                        <TableCell sx={{ width: '40%' }}>{inv.customerName}</TableCell>
                        <TableCell sx={{ width: '20%' }}>{inv.invoiceDate}</TableCell>
                        <TableCell sx={{ width: '20%' }}>{inv.total}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                        No invoices to display. Please search using date range.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Container>
        </LocalizationProvider>
      </Box>
    </ThemeProvider>
  );
}
